<template>
  <div class="batch-recharge-form">
    <!-- 收付款信息导入 -->
    <div class="form-section">
      <div class="section-label">
        {{ $t('basicData.ImportPaymentAndReceivingInformation') }}
      </div>
      <DragUploadCmp
        ref="paymentUploadRef"
        :auto-upload="false"
        :file-size="5"
        :file-type="paymentFileType"
        :limit="1"
        :multiple="false"
        :tips="$t('basicData.OnlySupportsXlsXlsxFiles')"
        @change="handlePaymentFileChange"
      />
      <div v-if="!paymentFileList.length" class="download-link" @click="downloadTemplate">
        {{ $t('basicData.DownloadTheTemplate') }}
      </div>
    </div>

    <!-- 回单文件导入 -->
    <div class="form-section">
      <div class="section-label">
        {{ $t('basicData.ImportReceiptFile') }}
      </div>
      <DragUploadCmp
        ref="receiptUploadRef"
        :file-size="10"
        :file-type="receiptFileType"
        :limit="20"
        :multiple="false"
        :tips="$t('basicData.FileOnlySupportsZipJpgJpegPngPdf')"
        @change="handleReceiptFileChange"
      />
    </div>

    <!-- 底部按钮 -->
    <div class="form-footer">
      <el-button size="small" @click="cancel">
        {{ $t('basicData.Cancel') }}
      </el-button>
      <el-button
        :loading="loading || parseLoading" size="small" type="primary"
        @click="confirm"
      >{{ $t('basicData.determine') }}
      </el-button>
    </div>
  </div>
</template>

<script>
import DragUploadCmp from './DragUploadCmp.vue';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
  apiFundAccountPage,
  apiFundAccountPage1,
  apiFundRechargeBatch
} from '@/api/finance/fundManagement'; // 模板数据行数
import i18n from '@/lang';
// 模板数据行数
const TEMPLATE_DATA_ROWS = 50;
// 数据起始行（表头占2行）
const DATA_START_ROW = 3;

// 模板列定义
const templateColumns = [
  { label: 'CNPJ', prop: 'cnpj' },
  { label: i18n.t('basicData.TransactionAmount'), prop: 'amount' },
  { label: i18n.t('basicData.TransactionDate'), prop: 'tradeDate' },
  { label: i18n.t('basicData.SerialNumber'), prop: 'serialNumber' },
  { label: i18n.t('basicData.ReceivingAccount'), prop: 'bankAccount' },
  { label: i18n.t('basicData.bankAttachmentFileName'), prop: 'receiptFileName' }
];

export default {
  name: 'BatchRechargeForm',

  components: { DragUploadCmp },

  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      loading: false,
      // 收付款信息附件列表
      paymentFileList: [],
      // 回单文件附件列表
      receiptFileList: [],
      // 收付款信息：仅支持 .xls, .xlsx
      paymentFileType: [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ],
      // 回单文件：支持 zip、JPG、JPEG、PNG、PDF
      receiptFileType: [
        'application/zip',
        'application/x-zip-compressed',
        'image/jpeg',
        'image/png',
        'application/pdf'
      ],
      // 银行账户列表（来自接口 apiFundAccountPage）
      bankCNPJList: [],
      accountList: [],
      // 解析后的收付款信息数据
      paymentData: [],
      // 解析加载状态
      parseLoading: false
    };
  },

  watch: {
    visible: {
      handler(newVal) {
        if (!newVal) {
          this.reset();
        }
      }
    }
  },

  created() {
    this.fetchCNPJList();
    this.fetchAccountList();
  },

  methods: {
    /**
     * 设置表头样式
     */
    setHeaderStyle(worksheet, row = 1) {
      const targetRow = worksheet.getRow(row);
      targetRow.font = {
        color: { argb: '000000' },
        bold: true
      };
      targetRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFF00' }
      };
    },

    /**
     * 列配置 → ExcelJS 列定义
     */
    toColumns(cols) {
      return cols.map((item) => ({
        header: item.label,
        key: item.prop,
        width: 30
      }));
    },

    /**
     * 收付款信息文件变化
     * 不上传 OSS，直接拿原始 File 解析
     */
    handlePaymentFileChange(fileList) {
      this.paymentFileList = fileList || [];
      const file = fileList && fileList[0];
      if (file && file.raw) {
        // 异步解析原始 File，不阻塞 UI
        this.parsePaymentExcel(file.raw).catch(() => {});
      } else {
        this.paymentData = [];
      }
    },

    /**
     * 单元格值转字符串（兼容 Date / 富文本 / 普通值）
     */
    cellToString(cellValue) {
      if (cellValue === null || cellValue === undefined) return '';
      if (cellValue instanceof Date) {
        // 格式化为 yyyy-MM-dd HH:mm:ss
        const pad = (n) => String(n).padStart(2, '0');
        return (
          cellValue.getFullYear() +
          '-' +
          pad(cellValue.getMonth() + 1) +
          '-' +
          pad(cellValue.getDate()) +
          ' ' +
          pad(cellValue.getHours()) +
          ':' +
          pad(cellValue.getMinutes()) +
          ':' +
          pad(cellValue.getSeconds())
        );
      }
      if (typeof cellValue === 'object') {
        // 富文本/超链接等情况，取 text 字段
        if (cellValue.text !== undefined) return String(cellValue.text);
        if (cellValue.result !== undefined) return String(cellValue.result);
        if (cellValue.richText) {
          return (cellValue.richText || []).map((t) => t.text).join('');
        }
        return JSON.stringify(cellValue);
      }
      return String(cellValue);
    },

    /**
     * 解析收付款信息 Excel（直接读取原始 File，不上传 OSS）
     * @param {File} rawFile 原始文件对象
     */
    async parsePaymentExcel(rawFile) {
      this.parseLoading = true;
      try {
        const buffer = await rawFile.arrayBuffer();
        const wb = new ExcelJS.Workbook();
        await wb.xlsx.load(buffer);
        const ws = wb.getWorksheet('Sheet1') || wb.worksheets[0];
        if (!ws) {
          this.paymentData = [];
          return [];
        }

        const result = [];
        ws.eachRow({ includeEmpty: false }, (row, rowNumber) => {
          if (rowNumber < DATA_START_ROW) return;
          const item = {
            cnpj: this.cellToString(row.getCell(1).value),
            amount: this.cellToString(row.getCell(2).value),
            tradeDate: this.cellToString(row.getCell(3).value),
            serialNumber: this.cellToString(row.getCell(4).value),
            bankAccount: this.cellToString(row.getCell(5).value),
            receiptFileName: this.cellToString(row.getCell(6).value)
          };
          // 跳过空行
          const hasValue = Object.values(item).some(
            (v) => v !== null && v !== undefined && v !== ''
          );
          if (hasValue) {
            result.push(item);
          }
        });

        this.paymentData = result;
        console.log('解析 Excel 成功，共', result.length, '条数据', result);
        // this.$message.warning(i18n.t('basicData.noTableDataParsed'));
        return result;
      } catch (err) {
        console.error('解析 Excel 失败', err);
        this.$message.error(i18n.t('basicData.excelParseFailed'));
        this.paymentData = [];
        return [];
      } finally {
        this.parseLoading = false;
      }
    },

    // cnpj
    fetchCNPJList() {
      apiFundAccountPage1({})
        .then((res) => {
          if (res.code === 200) {
            this.bankCNPJList = res?.data?.records || [];
          }
        })
        .catch(() => {
          this.bankCNPJList = [];
        });
    },
    fetchAccountList() {
      apiFundAccountPage({})
        .then((res) => {
          if (res.code === 200) {
            this.accountList = res?.data || [];
          }
        })
        .catch(() => {
          this.accountList = [];
        });
    },
    /**
     * 回单文件变化
     */
    handleReceiptFileChange(fileList) {
      this.receiptFileList = fileList || [];
    },

    /**
     * 下载模板
     */
    async downloadTemplate() {
      this.loading = true;
      try {
        const wb = new ExcelJS.Workbook();

        // Sheet1（主模板）
        const sheet1 = wb.addWorksheet('Sheet1');
        sheet1.columns = this.toColumns(templateColumns);
        this.setHeaderStyle(sheet1, 1);

        // 第2行：导入说明
        sheet1.getCell('A2').alignment = { wrapText: true, vertical: 'middle' };
        sheet1.getRow(2).height = 75;
        sheet1.mergeCells('A2:S2');
        sheet1.getCell('A2').value =
          '1、每次仅导入50条 /Importar apenas 50 itens de cada vez\n' +
          '2、所有字段均必填 /Todos os campos são obrigatórios\n' +
          '3、交易日期格式：2022-01-01 00:00:00 /Formato da Data da Transação: 2022-01-01 00:00:00\n' +
          '4、银行附件文件名：输入文件名，需要携带文件类型后缀，例如20260601-154434.jpg /Nome do arquivo do anexo bancário: informar o nome do arquivo incluindo a extensão\n' +
          '5、不可更改此模板 /Este modelo não pode ser alterado';
        sheet1.getRow(2).getCell(1).font = {
          color: { argb: 'ff0000' }
        };

        // 构建 CNPJ 下拉数据源（去重 + 过滤空值）
        const cnpjList = [
          ...new Set(
            (this.bankCNPJList || [])
              .map((item) => item && item.cnpjMother)
              .filter((v) => v !== undefined && v !== null && v !== '')
          )
        ];

        // 创建 CNPJ 隐藏数据源 Sheet
        if (cnpjList.length > 0) {
          const cnpjSheet = wb.addWorksheet('CNPJ');
          cnpjSheet.state = 'hidden';
          cnpjSheet.columns = [{ header: 'cnpjMother', key: 'cnpjMother', width: 30 }];
          cnpjSheet.addRows(cnpjList.map((cnpj) => ({ cnpjMother: cnpj })));

          // 为 50 行数据行的 CNPJ 列（列 1）设置下拉校验
          for (let i = 0; i < TEMPLATE_DATA_ROWS; i++) {
            const row = i + DATA_START_ROW;
            sheet1.getCell(row, 1).dataValidation = {
              type: 'list',
              allowBlank: true,
              formulae: [`'CNPJ'!$A$2:$A$${cnpjList.length + 1}`]
            };
          }
        }

        // 构建收款账号下拉数据源（取 companyName，去重 + 过滤空值）
        const accountList = [
          ...new Set(
            (this.accountList || [])
              .map((item) => item && item.companyName)
              .filter((v) => v !== undefined && v !== null && v !== '')
          )
        ];

        // 创建收款账号隐藏数据源 Sheet
        if (accountList.length > 0) {
          const accountSheet = wb.addWorksheet('收款账号');
          accountSheet.state = 'hidden';
          accountSheet.columns = [{ header: 'companyName', key: 'companyName', width: 30 }];
          accountSheet.addRows(accountList.map((name) => ({ companyName: name })));

          // 为 50 行数据行的收款账号列（列 5）设置下拉校验
          for (let i = 0; i < TEMPLATE_DATA_ROWS; i++) {
            const row = i + DATA_START_ROW;
            sheet1.getCell(row, 5).dataValidation = {
              type: 'list',
              allowBlank: true,
              formulae: [`'收款账号'!$A$2:$A$${accountList.length + 1}`]
            };
          }
        }

        // 交易日期列设为文本格式，避免 Excel 自动转换
        for (let i = 0; i < TEMPLATE_DATA_ROWS; i++) {
          const row = i + DATA_START_ROW;
          sheet1.getCell(row, 3).numFmt = '@';
        }

        const buffer = await wb.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/octet-stream' });
        saveAs(blob, i18n.t('basicData.rechargeImportTemplate'));
      } catch (err) {
        console.error('下载模板失败', err);
        this.$message.error('下载模板失败 / Falha ao baixar o modelo');
      } finally {
        this.loading = false;
      }
    },

    /**
     * 取消
     */
    cancel() {
      this.$emit('close');
    },

    /**
     * 确定
     */
    async confirm() {
      if (!this.paymentFileList.length) {
        this.$message.warning(this.$t('basicData.PleaseUploadExcelFiles'));
        return;
      }
      this.loading = true;
      try {
        // 确保解析完成（若上传后未完成解析，这里 await 等待）
        const file = this.paymentFileList[0];
        if (file && file.raw) {
          await this.parsePaymentExcel(file.raw);
        }

        if (!this.paymentData.length) {
          this.$message.warning(i18n.t('basicData.noTableDataParsed'));
          return;
        }

        // 回单文件必填校验
        if (!this.receiptFileList.length) {
          this.$message.warning(this.$t('basicData.pleaseUploadFile'));
          return;
        }

        // 通过 accountList 把 bankAccount（companyName）替换为 accountNumber
        const dataFile = this.paymentData.map((row) => {
          const accountItem = (this.accountList || []).find(
            (item) => item && item.companyName === row.bankAccount
          );
          return {
            ...row,
            bankAccount: accountItem ? accountItem.accountNumber : row.bankAccount
          };
        });

        // 组装接口请求参数
        const data = {
          dataFile,
          receiptFileList: this.receiptFileList.map((f) => ({ name: f.name, url: f.url }))
        };

        // 批量充值
        const res = await apiFundRechargeBatch(data);
        if (res.code === 200) {
          this.$message.success(this.$t('basicData.TheOperationSucceeded'));
          this.$emit('submit');
        }
      } catch (err) {
        console.error('批量充值失败', err);
      } finally {
        this.loading = false;
      }
    },

    /**
     * 重置
     */
    reset() {
      this.paymentFileList = [];
      this.receiptFileList = [];
      this.paymentData = [];
      if (this.$refs.paymentUploadRef) {
        this.$refs.paymentUploadRef.clearFiles();
      }
      if (this.$refs.receiptUploadRef) {
        this.$refs.receiptUploadRef.clearFiles();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.batch-recharge-form {
  padding: 0 4px;

  .form-section {
    margin-bottom: 20px;

    .section-label {
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 8px;
    }

    .download-link {
      margin-top: 8px;
      font-size: 13px;
      color: #00a551;
      cursor: pointer;
      user-select: none;
      width: 180px;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .form-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    padding-top: 12px;
    border-top: 1px solid #ebeef5;
  }
}
</style>
